import { nag, nagVC } from './errors.js';
import { isNonEmptyArray, maybeArray } from './nonEmptyArray.js';
import { scanSingleParamValue } from './scan.js';
import { isPropertyChar, nameToKey } from './utils.js';
import { atLeastOnceProperties, exactlyOnceProperties, isAtLeastOnceProperty, isAtMostOnceProperty, isExactlyOnceProperty, isKnownParameter, isKnownProperty, knownParameters, knownProperties, } from './vcard4Types.js';
/**
 * Parse an RFC 6350 (multi-)vCard input into an array and errors.
 * @param vcf Multiline string of potentially multiple vCards
 * @param keepErrors Whether very bad vCards should be deleted
 * @returns Array of vCards with metadata; array of global errors
 */
export function parseVCards(vcf, keepDefective) {
    if (keepDefective === void 0) { keepDefective = false; }
    var globalNags = [];
    // 1. unwrap: Be lenient in what we accept
    if (!vcf.includes('\r\n') && vcf.includes('\n')) {
        nag(globalNags, 'FILE_CRLF');
    }
    vcf = vcf.replace(/\r?\n[ \t]/g, '');
    // 2. process (unwrapped) line by line
    var vCardInProgress = { nags: [], unparseable: [] };
    var vCards = [];
    for (var _i = 0, _a = vcf.split(/\r?\n/); _i < _a.length; _i++) {
        var line = _a[_i];
        if (line === '') {
            continue; // Skip empty lines, required at the very end (optional otherwise)
        }
        parseLine(vCardInProgress, line);
        if (!('BEGIN' in vCardInProgress)) {
            vCardInProgress.didNotStartWithBEGIN = true;
        }
        if ('END' in vCardInProgress) {
            // Finish this one, ready for a potential next vCard
            var card = ensureCardinalities(globalNags, vCardInProgress, keepDefective);
            if (card) {
                vCards.push(card);
            }
            vCardInProgress = { nags: [], unparseable: [] };
        }
    }
    // vCard still in progress?
    if (interestingDataIn(vCardInProgress)) {
        var card = ensureCardinalities(globalNags, vCardInProgress, keepDefective);
        if (card) {
            vCards.push(card);
        }
    }
    // Clean return value
    var retval = { vCards: maybeArray(vCards), nags: maybeArray(globalNags) };
    if (!retval.vCards) {
        delete retval.vCards;
    }
    if (!retval.nags) {
        delete retval.nags;
    }
    return retval;
}
function interestingDataIn(partialVCard) {
    return (Object.entries(partialVCard).length > 2 ||
        partialVCard.nags.length > 0 ||
        partialVCard.unparseable.length > 0);
}
/**
 * Turn partial vCard either into a full one (with optional errors) or reject it entirely.
 * @param globalNags The global nag list to potentially append to
 * @param partialVCard A partial vCard to either turn into a complete card or refuse
 * @param keepDefective Whether to keep vCards with major defects
 * @returns A full vCard (or null, if too defective)
 */
function ensureCardinalities(globalNags, partialVCard, keepDefective) {
    var _a, _b, _c, _d;
    // Major problems
    if (partialVCard.didNotStartWithBEGIN === true) {
        if (keepDefective) {
            nagVC(partialVCard.nags, 'VCARD_NOT_BEGIN', { property: 'BEGIN' });
        }
        else {
            nag(globalNags, 'VCARD_NOT_BEGIN');
            return null;
        }
    }
    for (var _i = 0, _e = ['BEGIN', 'END']; _i < _e.length; _i++) {
        var property = _e[_i];
        if (((_b = (_a = partialVCard[property]) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.toUpperCase()) !== 'VCARD') {
            if (keepDefective) {
                nagVC(partialVCard.nags, 'VCARD_BAD_TYPE', { property: property });
            }
            else {
                nag(globalNags, 'VCARD_BAD_TYPE');
                return null;
            }
        }
    }
    // Cardinality '1' properties
    for (var _f = 0, _g = Object.entries(exactlyOnceProperties); _f < _g.length; _f++) {
        var _h = _g[_f], k = _h[0], expectedValue = _h[1];
        if (isExactlyOnceProperty(k)) {
            // Type guard needed to make the compiler happy
            if (!(k in partialVCard)) {
                nagVC(partialVCard.nags, 'VCARD_MISSING_PROP', { property: k });
                partialVCard[k] = { value: expectedValue };
            }
            else {
                if (partialVCard[k].value.toUpperCase() !== expectedValue) {
                    nagVC(partialVCard.nags, 'VALUE_INVALID', {
                        property: k,
                        line: "".concat(k, ":").concat(partialVCard[k].value),
                    });
                }
            }
        }
    }
    // Cardinality '1*' properties
    for (var _j = 0, _k = Object.entries(atLeastOnceProperties); _j < _k.length; _j++) {
        var _l = _k[_j], k = _l[0], defaultValue = _l[1];
        if (isAtLeastOnceProperty(k)) {
            // Type guard needed to make the compiler happy
            if (!(k in partialVCard)) {
                nagVC(partialVCard.nags, 'VCARD_MISSING_PROP', { property: k });
                partialVCard[k] = [{ value: defaultValue }];
            }
        }
    }
    // Clean: Remove undefined parameters
    for (var _m = 0, _o = Object.entries(partialVCard); _m < _o.length; _m++) {
        var key = _o[_m][0];
        var k = key;
        if (isKnownProperty(k)) {
            if (isAtMostOnceProperty(k) || isExactlyOnceProperty(k)) {
                var v = partialVCard[k];
                if ('parameters' in v &&
                    (!v.parameters || Object.keys(v.parameters).length === 0)) {
                    delete v.parameters;
                }
            }
            else {
                var v = partialVCard[k];
                for (var i in v) {
                    var vi = v[i]; // Improves type checks
                    if ('parameters' in vi &&
                        (!vi.parameters || Object.keys(vi.parameters).length === 0)) {
                        delete vi.parameters;
                    }
                }
            }
        }
    }
    for (var _p = 0, _q = Object.entries((_c = partialVCard.x) !== null && _c !== void 0 ? _c : {}); _p < _q.length; _p++) {
        var _r = _q[_p], k = _r[0], v = _r[1];
        for (var i in v) {
            var vi = v[i]; // Improves type checks
            if ('parameters' in vi &&
                (!vi.parameters || Object.keys(vi.parameters).length === 0)) {
                delete vi.parameters;
            }
        }
    }
    /*
     * Until the `return` statement, we are in a transition:
     * At the beginning, `nags` and `unparseable` are arrays, defined but
     * possibly empty; at the end, both are optional `NonEmptyArray`s.
     * This is not easily reflected in types, but we're trying.
     */
    var transitionVCard = partialVCard;
    // Clean Nags; set hasErrors
    if (isNonEmptyArray(transitionVCard.nags)) {
        transitionVCard.hasErrors = ((_d = transitionVCard.nags) !== null && _d !== void 0 ? _d : [])
            .map(function (nag) { return nag.isError; })
            .reduce(function (a, b) { return a || b; }, false);
    }
    else {
        transitionVCard.hasErrors = false;
        delete transitionVCard.nags;
    }
    // Clean `unparseable`
    transitionVCard.unparseable = maybeArray(transitionVCard.unparseable);
    if (!transitionVCard.unparseable) {
        delete transitionVCard.unparseable;
    }
    // Clean possible internal flags
    delete transitionVCard.didNotStartWithBEGIN;
    // All the required fields are here now, great!
    return transitionVCard;
}
/**
 * Parse a line and extend the partial vCard accordingly.
 * @param vCardInProgress The vCard to add the line to, if possible
 * @param line The new line
 */
export function parseLine(vCardInProgress, line) {
    var _a;
    var propertyInfo = extractProperty(line, vCardInProgress.nags);
    if (!propertyInfo) {
        vCardInProgress.unparseable.push(line);
        return;
    }
    var property = propertyInfo.property;
    var parameterInfo = parseParameters(line, propertyInfo.end, vCardInProgress.nags, property);
    if (!parameterInfo) {
        // Has already been nagged
        vCardInProgress.unparseable.push(line);
        return;
    }
    var parameters = parameterInfo.parameters;
    if (line.charAt(parameterInfo.end) !== ':') {
        // Should not happen
        nagVC(vCardInProgress.nags, 'PROP_MISSING_COLON', { property: property, line: line });
        vCardInProgress.unparseable.push(line);
        return;
    }
    var rawValue = line.substring(parameterInfo.end + 1);
    if (isKnownProperty(property)) {
        // Obtain the value parsed into the right type.
        // Using the property's `<Type>parse()` ensures that we have the proper type,
        // however, to convince the compiler of the same, an 8-way condition would
        // be necessary for each of the three `vCardInProgress[property]` assignments…
        var parsedValue = knownProperties[property].parse(rawValue, function (error) {
            nagVC(vCardInProgress.nags, error, { property: property, line: line });
        });
        if (isExactlyOnceProperty(property) || isAtMostOnceProperty(property)) {
            // Deal with property cardinality
            if (property in vCardInProgress) {
                // Too many; ignore all but first
                nagVC(vCardInProgress.nags, 'PROP_DUPLICATE', { property: property, line: line });
                vCardInProgress.unparseable.push(line);
                return;
            }
            else {
                vCardInProgress[property] = { parameters: parameters, value: parsedValue };
            }
        }
        else {
            // Multiple properties: AtLeastOnce, AnyCardinality
            if (property in vCardInProgress) {
                vCardInProgress[property].push({ parameters: parameters, value: parsedValue });
            }
            else {
                vCardInProgress[property] = [{ parameters: parameters, value: parsedValue }];
            }
        }
    }
    else {
        // Add x property
        (_a = vCardInProgress.x) !== null && _a !== void 0 ? _a : (vCardInProgress.x = {});
        if (property in vCardInProgress.x) {
            vCardInProgress.x[property].push({
                parameters: parameters,
                value: rawValue,
            });
        }
        else {
            vCardInProgress.x[property] = [{ parameters: parameters, value: rawValue }];
        }
    }
}
/**
 * Scan a group or property name.
 * @param line The entire line
 * @param start The offset at which to start scanning
 * @returns The scanned name and end position in upper case, or throws an error
 */
export function scanPropertyOrGroup(line, start, nags) {
    for (var i = start; i < line.length; i++) {
        if (!isPropertyChar(line.charAt(i))) {
            if (start === i) {
                nagVC(nags, 'PROP_NAME_EMPTY', { property: '', line: line });
                return null;
            }
            else {
                return { name: nameToKey(line.substring(start, i)), end: i };
            }
        }
    }
    nagVC(nags, 'PROP_NAME_EOL', { property: line.substring(start), line: line });
    return null;
}
/**
 * Extract (group and) property names.
 * @param line The line to be scanned (from start)
 * @returns (Optional) group, property (both in uppercase), and scanning end. Or null on error.
 */
export function extractProperty(line, nags) {
    var part1 = scanPropertyOrGroup(line, 0, nags);
    if (part1 === null) {
        return null;
    }
    if (line.charAt(part1.end) !== '.') {
        // Ordinary property
        return { property: part1.name, end: part1.end };
    }
    else {
        // Grouped property
        var part2 = scanPropertyOrGroup(line, part1.end + 1, nags);
        if (part2 === null) {
            return null;
        }
        else {
            return { group: part1.name, property: part2.name, end: part2.end };
        }
    }
}
/**
 * Scan a lone parameter value, without unescaping it
 * @param line The line to be parsed
 * @param start The offset to start parsing at (*after* the equals sign!)
 * @returns The parsed value and the end offset of it
 */
export function scanParamRawValue(line, start) {
    var index = start;
    while (index < line.length && !':;'.includes(line.charAt(index))) {
        index++;
    }
    return { value: line.substring(start, index), end: index };
}
/**
 * Scan the potentially multiple values of this parameter.
 * @param line The line currently being parsed
 * @param start The offset to start parsing at
 * @param property The name of the property this parameter belongs to
 * @param parameter The name of the parameter
 * @param nags The list of all complaints
 * @param singleValue Do not treat commas as separator
 * @returns A string[] value and a parsing end, or null
 */
export function scanParamValues(line, start, property, parameter, nags, singleValue) {
    if (singleValue === void 0) { singleValue = false; }
    var moreItemsChar = singleValue ? '' : ',';
    var separatorChars = singleValue ? ';:' : ',;:';
    var index = start;
    var parameterValues = [];
    var unescapedComma = false;
    while (line.charAt(index) === (parameterValues.length === 0 ? '=' : moreItemsChar)) {
        index++;
        if (line.charAt(index) === ',') {
            unescapedComma = true;
        }
        if (line.charAt(index) === '"') {
            // Quoted value
            var closingQuote = line.indexOf('"', index + 1);
            if (closingQuote < 0) {
                nagVC(nags, 'PARAM_UNCLOSED_QUOTE', { property: property, parameter: parameter, line: line });
                return null;
            }
            parameterValues.push(scanSingleParamValue(line.substring(index + 1, closingQuote), function (error) {
                nagVC(nags, error, { property: property, parameter: parameter, line: line });
            }));
            index = closingQuote + 1;
        }
        else {
            // Potentially escaped value
            var currentValue = '';
            while (index < line.length &&
                !separatorChars.includes(line.charAt(index))) {
                if (line.charAt(index) === ',') {
                    unescapedComma = true;
                }
                if (line.charAt(index) === '\\') {
                    var escaped = line.charAt(index + 1);
                    if (escaped.toUpperCase() === 'N') {
                        currentValue += '\n';
                    }
                    else {
                        currentValue += escaped;
                    }
                    index += 2;
                }
                else {
                    currentValue += line.charAt(index++);
                }
            }
            parameterValues.push(currentValue);
        }
    }
    if (unescapedComma) {
        // Only warn once per parameter
        nagVC(nags, 'PARAM_UNESCAPED_COMMA', { property: property, parameter: parameter, line: line });
    }
    return { value: parameterValues, end: index };
}
/**
 * Scan a lone parameter value, unescaping it
 * @param line The line to be parsed
 * @param start The offset to start parsing at (the equals sign)
 * @param property The name of the property this parameter belongs to
 * @param parameter The name of the parameter
 * @param nags The list of all complaints
 * @returns The parsed value and the end offset of it
 */
export function scanParamValue(line, start, property, parameter, nags) {
    var result = scanParamValues(line, start, property, parameter, nags, true);
    if (result) {
        /* istanbul ignore else */
        if (result.value.length === 1) {
            return { value: result.value[0], end: result.end };
        }
        else {
            // Should not happen anymore since the support of `singleValue`
            nagVC(nags, 'PARAM_NOT_SINGLE', { property: property, parameter: parameter, line: line });
            return null;
        }
    }
    else {
        return null;
    }
}
/**
 * Parse the parameters. Used after scanning the property or group. What remains should be the value.
 * @param line The vCard line to be analyzed
 * @param start The offset where to start parsing (the equals sign)
 * @returns A record of VCardParameters, and the end offset.
 */
export function parseParameters(line, start, nags, property) {
    var _a;
    var index = start;
    var parameters = {};
    while (line.charAt(index) === ';') {
        // Parse another property: Name
        index++;
        var start_1 = index;
        while (isPropertyChar(line.charAt(index))) {
            index++;
        }
        var parameterName = nameToKey(line.substring(start_1, index));
        if (line.charAt(index) !== '=') {
            nagVC(nags, 'PARAM_MISSING_EQUALS', {
                property: property,
                parameter: parameterName,
                line: line,
            });
            return null;
        }
        // Index still points at the equals sign!
        // Value(s)
        if (isKnownParameter(parameterName)) {
            switch (knownParameters[parameterName].name) {
                case 'string':
                case 'number':
                    {
                        if (parameterName in parameters) {
                            nagVC(nags, 'PARAM_DUPLICATE', {
                                property: property,
                                parameter: parameterName,
                                line: line,
                            });
                        }
                        var retval_1 = scanParamValue(line, index, property, parameterName, nags);
                        if (retval_1) {
                            index = retval_1.end;
                            if (knownParameters[parameterName].name === 'number') {
                                var num = parseInt(retval_1.value, 10);
                                if (isFinite(num)) {
                                    parameters[parameterName] = num;
                                }
                                else {
                                    nagVC(nags, 'PARAM_INVALID_NUMBER', {
                                        property: property,
                                        parameter: parameterName,
                                        line: line,
                                    });
                                }
                            }
                            else {
                                parameters[parameterName] = retval_1.value;
                            }
                        }
                        else {
                            return null;
                        }
                    }
                    break;
                case 'string[+]':
                    // Re-scan the `=`
                    var values = scanParamValues(line, index, property, parameterName, nags);
                    if (values == null) {
                        return null;
                    }
                    else {
                        if (parameterName in parameters) {
                            parameters[parameterName] = parameters[parameterName].concat(values.value);
                        }
                        else {
                            parameters[parameterName] = values.value;
                        }
                        index = values.end;
                    }
                    break;
                /* istanbul ignore next */
                default:
                    // Skip over this unknown type; should never happen
                    var retval = scanParamValue(line, index, property, parameterName, nags);
                    nagVC(nags, 'PARAM_INVALID_TYPE', {
                        property: property,
                        parameter: parameterName,
                        line: line,
                    });
                    if (retval) {
                        index = retval.end;
                    }
                    else {
                        return null;
                    }
            }
        }
        else {
            (_a = parameters.x) !== null && _a !== void 0 ? _a : (parameters.x = {});
            // scanParamRawValue, unlike scanParamValue(s), starts *after* the equals sign
            var _b = scanParamRawValue(line, index + 1), value = _b.value, end = _b.end;
            if (parameterName in parameters.x) {
                // This is inconsistent:
                // `KEY=value;KEY=v2` results in `{KEY: ['value', 'v2']}`, whereas
                // `KEY=value,v2` results in `{KEY: ['value,v2']}`.
                // However, this is the only way not to make assumptions about the value type.
                parameters.x[parameterName].push(value);
            }
            else {
                parameters.x[parameterName] = [value];
            }
            index = end;
        }
    }
    return { parameters: parameters, end: index };
}
//# sourceMappingURL=parse.js.map